import { useCustomMutation } from '@refinedev/core';
import * as Sentry from '@sentry/react';
import { useCallback } from 'react';
import { graphqlErrorInfo } from '../../utils/graphqlErrors';
import { COMMAND_FIELDS } from './model';

/**
 * E38 · the practice's safety-plan commands from the portal — UpdateSafetyPlanSectionInSession ("Edit with the client")
 * and ReviewSafetyPlan ("Mark reviewed") — each ONE deliberate act (a button), posted with `errorNotification: false` so
 * the screen reports the outcome itself: a CODED refusal (`safetyPlan.errors.*`, already in the therapist's language) is
 * shown verbatim; anything else is the screen's own copy plus Sentry.
 */
export type SafetyPlanOperation =
  | 'updateSafetyPlanSectionInSession'
  | 'reviewSafetyPlan';

const INPUT_TYPE: Record<SafetyPlanOperation, string> = {
  updateSafetyPlanSectionInSession: 'UpdateSafetyPlanSectionInSessionInput!',
  reviewSafetyPlan: 'ReviewSafetyPlanInput!',
};

export type SafetyPlanCommandResult =
  | { ok: true; planId: string; recorded: boolean }
  | { ok: false; code: string | undefined; message: string };

export const useSafetyPlanCommand = () => {
  const { mutateAsync, isLoading } = useCustomMutation<{
    planId: string;
    recorded: boolean;
  }>();

  const run = useCallback(
    async (
      operation: SafetyPlanOperation,
      input: Record<string, unknown>,
      fallback: string,
    ): Promise<SafetyPlanCommandResult> => {
      try {
        const res = await mutateAsync({
          url: '',
          method: 'post',
          values: {},
          errorNotification: false,
          successNotification: false,
          meta: {
            operation,
            fields: COMMAND_FIELDS,
            variables: {
              input: { name: 'input', type: INPUT_TYPE[operation], value: input },
            },
          },
        });
        const data = res?.data;
        if (!data?.planId) {
          // a null answer is a refusal, never a save (portal conventions)
          return { ok: false, code: undefined, message: fallback };
        }
        return { ok: true, planId: data.planId, recorded: !!data.recorded };
      } catch (error) {
        const info = graphqlErrorInfo(error);
        if (info.code && info.message) {
          return { ok: false, code: info.code, message: info.message };
        }
        Sentry.captureException(error);
        return { ok: false, code: info.code, message: fallback };
      }
    },
    [mutateAsync],
  );

  return { run, isLoading };
};
